import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  makeStyles,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@material-ui/core';
import { Close, LocalOffer, ShoppingCart, CheckCircleOutline, InfoOutlined } from '@material-ui/icons';
import { useSelector, useDispatch } from 'src/store';
import {
  toggleCart,
  applyCoupon,
  removeCoupon,
  clearCart,
} from 'src/slices/cart';
import CartItem from './CartItem';
import { getNavigatorLanguage } from 'src/utils/locale';
import { Theme } from 'src/theme';
import { useSnackbar } from 'notistack';
import { getCurrentSubscriptions } from 'src/requests/subscriptions';
import { addAddOn } from 'src/requests/addOns';
import useAuth from 'src/hooks/useAuth';

const useStyles = makeStyles((theme: Theme) => ({
  drawer: {
    width: 480,
    [theme.breakpoints.down('sm')]: {
      width: '100vw',
    },
  },
  drawerPaper: {
    width: 480,
    [theme.breakpoints.down('sm')]: {
      width: '100vw',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  itemsList: {
    flex: 1,
    padding: theme.spacing(2, 2, 1, 2),
    overflowY: 'auto',
  },
  footer: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
  couponSection: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
  },
  totalSection: {
    marginBottom: theme.spacing(2),
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  discountRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    color: theme.palette.success.main,
  },
  finalTotal: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: theme.typography.fontWeightBold as any,
    fontSize: '1.125rem',
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  checkoutButton: {
    borderRadius: 4,
    padding: theme.spacing(1.5),
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    textTransform: 'none',
  },
  clearButton: {
    borderRadius: 4,
    color: theme.palette.text.secondary,
    textTransform: 'none',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.error.main,
    },
  },
  emptyCart: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: 300,
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  appliedCoupon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.success.light + '20',
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.success.light}`,
  },
  couponInput: {
    '& .MuiOutlinedInput-root': {
      boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.08), 0px 1px 1px rgba(0, 0, 0, 0.04)',
      backgroundColor: theme.palette.background.paper,
      borderRadius: '8px',
      fontFamily: 'Roboto',
      fontWeight: 500,
      fontSize: '16px',
      '& fieldset': {
        border: 'none',
      },
      '&:hover fieldset': {
        border: 'none',
      },
      '&.Mui-focused fieldset': {
        border: 'none',
        outline: `${theme.palette.primary.main} solid 1px`,
      },
    },
    '& .MuiInputBase-input': {
      padding: '12px 14px',
    },
  },
  couponButton: {
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 600,
    minWidth: 70,
  },
  confirmDialog: {
    '& .MuiDialog-paper': {
      borderRadius: 16,
      minWidth: 500,
      maxWidth: 600,
      [theme.breakpoints.down('sm')]: {
        minWidth: '90vw',
      },
    },
  },
  confirmDialogTitle: {
    padding: theme.spacing(3, 3, 2, 3),
  },
  confirmTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  confirmIcon: {
    color: theme.palette.primary.main,
    fontSize: 40,
  },
  confirmDialogContent: {
    padding: theme.spacing(2, 3, 3, 3),
  },
  billingInfoBox: {
    backgroundColor: theme.palette.primary.main + '08',
    border: `2px solid ${theme.palette.primary.main}20`,
    borderRadius: 12,
    padding: theme.spacing(2.5),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  billingInfoTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    color: theme.palette.primary.main,
    '& svg': {
      verticalAlign: 'middle',
    },
  },
  billingInfoItem: {
    display: 'flex',
    alignItems: 'flex-start',
    marginTop: theme.spacing(1.5),
    '&:first-child': {
      marginTop: theme.spacing(1),
    },
  },
  bulletPoint: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    marginRight: theme.spacing(1.5),
    marginTop: 8,
    flexShrink: 0,
  },
  confirmDialogActions: {
    padding: theme.spacing(2, 3, 3, 3),
    gap: theme.spacing(1.5),
  },
  confirmButton: {
    borderRadius: 10,
    padding: theme.spacing(1.5, 4),
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '1rem',
    boxShadow: theme.shadows[2],
    '&:hover': {
      boxShadow: theme.shadows[4],
    },
  },
  cancelButton: {
    borderRadius: 10,
    padding: theme.spacing(1.5, 3),
    textTransform: 'none',
    fontSize: '1rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

const CartDrawer: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { isOpen, items, totalPrice, totalItems, appliedDiscount, coupon, isApplyingCoupon } =
    useSelector((state) => state.cart);

  const [localCouponCode, setLocalCouponCode] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleClose = () => {
    dispatch(toggleCart(false));
  };

  const handleCouponCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalCouponCode(event.target.value);
  };

  const handleApplyCoupon = async () => {
    if (localCouponCode.trim()) {
      const result = await applyCoupon(localCouponCode.trim(), dispatch);
      if (result.success) {
        enqueueSnackbar(result.message, { variant: 'success' });
        setLocalCouponCode('');
      } else {
        enqueueSnackbar(result.message, { variant: 'error' });
      }
    }
  };

  const handleRemoveCoupon = () => {
    dispatch(removeCoupon());
    setLocalCouponCode('');
  };

  const handleClearCart = () => {
    dispatch(clearCart());
  };

  const handleCheckoutClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmCheckout = async () => {
    setShowConfirmDialog(false);
    await handleCheckout();
  };

  const handleCancelCheckout = () => {
    setShowConfirmDialog(false);
  };

  const handleCheckout = async () => {
    setIsProcessing(true);

    try {
      const currentSubscriptions = await getCurrentSubscriptions(user.organization.organizationId);

      if (!currentSubscriptions || currentSubscriptions.length === 0) {
        enqueueSnackbar('No active subscription found. Please create a subscription first.', {
          variant: 'error',
        });
        return;
      }

      const mainSubscription = currentSubscriptions[0];
      const allCartPriceIds = items.flatMap((item) => Array(item.quantity).fill(item.priceId));

      const response = await addAddOn(
        user.organization.organizationId,
        mainSubscription.subscriptionId,
        allCartPriceIds
      );

      if (response?.error) {
        throw new Error('Failed to add items to subscription');
      }

      dispatch(clearCart());
      dispatch(toggleCart(false));

      enqueueSnackbar(
        `Successfully added ${items.length} ${items.length === 1 ? 'item' : 'items'} to your subscription!`,
        { variant: 'success' }
      );

      window.dispatchEvent(new CustomEvent('subscription-updated'));
    } catch (error) {
      enqueueSnackbar('Failed to complete checkout. Please try again or contact support.', {
        variant: 'error',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const formatPrice = (amount: number, currency = 'USD') => {
    return new Intl.NumberFormat(getNavigatorLanguage(), {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return (
    <>
      <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleClose}
      className={classes.drawer}
      classes={{ paper: classes.drawerPaper }}
    >
      <Box className={classes.content}>
        <Box className={classes.header}>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Shopping Cart ({totalItems})
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>

        {items.length === 0 ? (
          <Box className={classes.emptyCart}>
            <ShoppingCart style={{ fontSize: 64, marginBottom: 16 }} />
            <Typography variant="h6" gutterBottom>
              Your cart is empty
            </Typography>
            <Typography variant="body2">
              Browse our marketplace to add products to your cart
            </Typography>
          </Box>
        ) : (
          <>
            <Box className={classes.itemsList}>
              {items.map((item, index) => (
                <CartItem key={`${item.product.productId}-${item.interval}-${index}`} item={item} />
              ))}
            </Box>

            <Box className={classes.footer}>
              <Box className={classes.couponSection}>
                <Box display="flex" alignItems="center" mb={1.5}>
                  <LocalOffer style={{ marginRight: 8, color: '#1976d2' }} />
                  <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    Have a coupon?
                  </Typography>
                </Box>

                {coupon ? (
                  <Box className={classes.appliedCoupon}>
                    <Chip
                      label={`${coupon.promoCode} - ${coupon.type === 'percentOff' ? `${coupon.value}% off` : formatPrice(coupon.value)}`}
                      color="primary"
                      icon={<LocalOffer />}
                      style={{ fontWeight: 600 }}
                    />
                    <Button size="small" onClick={handleRemoveCoupon} style={{ color: '#666' }}>
                      Remove
                    </Button>
                  </Box>
                ) : (
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    style={{ gap: 12 }}
                  >
                    <Box width="65%">
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="COUPON1234"
                        value={localCouponCode}
                        onChange={handleCouponCodeChange}
                        className={classes.couponInput}
                      />
                    </Box>
                    <Box width="25%">
                      <Button
                        color="secondary"
                        disabled={!localCouponCode.trim() || isApplyingCoupon}
                        fullWidth
                        size="large"
                        variant="contained"
                        className={classes.couponButton}
                        onClick={handleApplyCoupon}
                      >
                        {isApplyingCoupon ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          'Apply'
                        )}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>

              <Box className={classes.totalSection}>
                <Box className={classes.totalRow}>
                  <Typography variant="body2">Subtotal:</Typography>
                  <Typography variant="body2">{formatPrice(subtotal)}</Typography>
                </Box>

                {appliedDiscount > 0 && (
                  <Box className={classes.discountRow}>
                    <Typography variant="body2">Discount:</Typography>
                    <Typography variant="body2">-{formatPrice(appliedDiscount)}</Typography>
                  </Box>
                )}

                <Box className={classes.finalTotal}>
                  <Typography variant="h6">Total:</Typography>
                  <Typography variant="h6">{formatPrice(totalPrice)}</Typography>
                </Box>
              </Box>

              <Button
                fullWidth
                variant="contained"
                color="primary"
                className={classes.checkoutButton}
                onClick={handleCheckoutClick}
                disabled={isProcessing}
                startIcon={isProcessing ? <CircularProgress size={20} /> : <ShoppingCart />}
              >
                {isProcessing ? 'Processing...' : 'Checkout All Items'}
              </Button>

              <Button
                fullWidth
                variant="text"
                className={classes.clearButton}
                onClick={handleClearCart}
              >
                Clear Cart
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Drawer>

      <Dialog open={showConfirmDialog} onClose={handleCancelCheckout} className={classes.confirmDialog}>
        <DialogTitle className={classes.confirmDialogTitle}>
          <Box className={classes.confirmTitle}>
            <CheckCircleOutline className={classes.confirmIcon} />
            <Typography variant="h4" style={{ fontWeight: 700, fontSize: '1.75rem' }}>
              Confirm Checkout
            </Typography>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent className={classes.confirmDialogContent}>
          <Typography variant="h6" color="textPrimary" style={{ fontSize: '1.125rem', marginBottom: 8 }}>
            You are about to add <strong>{totalItems}</strong> {totalItems === 1 ? 'item' : 'items'} to your subscription.
          </Typography>

          <Box className={classes.billingInfoBox}>
            <Box className={classes.billingInfoTitle}>
              <InfoOutlined style={{ fontSize: 24 }} />
              <Typography variant="subtitle1" style={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1 }}>
                How billing works
              </Typography>
            </Box>

            <Box className={classes.billingInfoItem}>
              <Box className={classes.bulletPoint} />
              <Typography variant="body2" style={{ fontSize: '0.95rem', lineHeight: 1.6 }}>
                The cost will be <strong>prorated</strong> based on your current billing cycle
              </Typography>
            </Box>

            <Box className={classes.billingInfoItem}>
              <Box className={classes.bulletPoint} />
              <Typography variant="body2" style={{ fontSize: '0.95rem', lineHeight: 1.6 }}>
                Your credit card will be charged when your subscription renews
              </Typography>
            </Box>

            <Box className={classes.billingInfoItem}>
              <Box className={classes.bulletPoint} />
              <Typography variant="body2" style={{ fontSize: '0.95rem', lineHeight: 1.6 }}>
                No immediate charge will be made to your account
              </Typography>
            </Box>
          </Box>

          <Typography variant="body1" color="textPrimary" style={{ fontWeight: 600 }}>
            Are you sure you want to proceed?
          </Typography>
        </DialogContent>

        <DialogActions className={classes.confirmDialogActions}>
          <Button
            onClick={handleCancelCheckout}
            className={classes.cancelButton}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmCheckout}
            variant="contained"
            color="primary"
            className={classes.confirmButton}
            disabled={isProcessing}
            startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isProcessing ? 'Processing...' : 'Add to Subscription'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CartDrawer;
